import { CalculatorOutlined, FolderAddOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import clsx from 'clsx';
import { useAuthContext } from 'contexts/AuthContext';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { AccessType } from 'types/User';
import AjoutDossierForm from 'views/dossiers/ajout/AjoutDossierForm';
import NouveauDossierConcessionnaire from 'views/dossiers/concessionnaire/NouveauDossierConcessionnaire.page';

type NewDossierButtonProps = {
  showOnMobile?: boolean;
};

const NewDossierButton: React.FC<NewDossierButtonProps> = ({
  showOnMobile = false,
}) => {
  const { t } = useTranslation();
  const { auth } = useAuthContext();
  const location = useLocation();
  const [openRequestId, setOpenRequestId] = useState(0);
  const [isConcessionnaireModalOpen, setIsConcessionnaireModalOpen] =
    useState(false);
  const [concessionnaireInitialStep, setConcessionnaireInitialStep] =
    useState(1);

  const isConcessionnaire =
    auth?.user?.accessType === AccessType.CONCESSIONNAIRE;
  const isSimulateur = auth?.user?.accessType === AccessType.SIMULATEUR;
  const isClient = auth?.user?.accessType === AccessType.CLIENT;
  const wrapperClassName = clsx(
    showOnMobile ? 'contents' : 'hidden sm:contents',
  );

  const handleOpenModal = () => {
    if (isClient) {
      setOpenRequestId((previous) => previous + 1);
      return;
    }

    if (isConcessionnaire) {
      setConcessionnaireInitialStep(1);
      setIsConcessionnaireModalOpen(true);
    }
  };

  useEffect(() => {
    if (!isConcessionnaire) {
      return;
    }

    const query = new URLSearchParams(location.search);
    if (query.get('openNouveauDossier') !== '1') {
      return;
    }

    const rawStep = Number(query.get('nouveauDossierStep'));
    const normalizedStep =
      Number.isFinite(rawStep) && rawStep >= 1 && rawStep <= 3
        ? Math.floor(rawStep)
        : 1;

    setConcessionnaireInitialStep(normalizedStep);
    setIsConcessionnaireModalOpen(true);
  }, [isConcessionnaire, location.search]);

  const buttonContent = (
    <Button
      type="primary"
      size="large"
      shape="round"
      icon={
        isSimulateur ? (
          <CalculatorOutlined className="text-lg" />
        ) : (
          <FolderAddOutlined />
        )
      }
      onClick={isClient || isConcessionnaire ? handleOpenModal : undefined}
    >
      <span className="pb-0.5">
        {isSimulateur
          ? t('global:calculer_ma_prime')
          : t('global:soumettre_un_dossier')}
      </span>
    </Button>
  );

  if (isClient) {
    return (
      <>
        <span className={wrapperClassName}>{buttonContent}</span>
        <AjoutDossierForm openRequestId={openRequestId} />
      </>
    );
  }

  if (isConcessionnaire) {
    return (
      <>
        <span className={wrapperClassName}>{buttonContent}</span>
        <NouveauDossierConcessionnaire
          open={isConcessionnaireModalOpen}
          initialStep={concessionnaireInitialStep}
          onClose={() => setIsConcessionnaireModalOpen(false)}
        />
      </>
    );
  }

  return (
    <span className={wrapperClassName}>
      <Link to={isSimulateur ? '/calculer-prime' : '#'}>{buttonContent}</Link>
    </span>
  );
};

export default NewDossierButton;
